import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../core/data/redux/store";
import {
  fetchItDeclarationTypes,
  fetchItDeclarationSubTypes,
  fetchItDeclarationPropertyTypes,
  fetchItDeclarationConfigSets,
  fetchItDeclarationContentSets,
  submitItDeclaration,
} from "../../../core/data/redux/itDeclarationSlice";
import { all_routes } from "../../router/all_routes";
import { toast } from "react-toastify";
import {
  getLinePopupKind,
  getDetailHelpText,
  EVIDENCE_TEXT_KEY,
  LinePopupKind,
  parseBackendError,
} from "./lineItemPopupUtils";

const routes = all_routes;

interface Option {
  label: string;
  value: string;
}

// Blocked SAP keys that must not be sent directly by employee in ToDynamics (SAP-managed)
const BLOCKED_SAP_DYNAMICS = new Set(["RQAMT", "APAMT"]);

const customerFieldKey = (field: any, source: "header" | "line"): string => {
  const raw =
    source === "line"
      ? field?.LineCustomerField || field?.HeaderCustomerField || ""
      : field?.HeaderCustomerField || "";
  return String(raw).trim();
};

const ITDeclarationCreatePage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  const authUserObj = useSelector((state: RootState) => (state as any).auth?.user);
  const reduxUser = authUserObj?.data?.data?.user || authUserObj?.user || authUserObj;

  const localUserStr = localStorage.getItem("user") || localStorage.getItem("userData");
  let localUser: any = null;
  try {
    localUser = localUserStr ? JSON.parse(localUserStr) : null;
  } catch (e) {}

  const activeUser =
    reduxUser?.empId || reduxUser?.usrid
      ? reduxUser
      : localUser?.data?.data?.user || localUser?.user || localUser || {};

  const empId = String(
    activeUser?.details?.empId ||
      activeUser?.empId ||
      activeUser?.usrid ||
      activeUser?.employeeNumber ||
      activeUser?.id ||
      ""
  );
  const userName = `${
    activeUser?.details?.firstname || activeUser?.firstname || activeUser?.name || "Employee"
  } ${activeUser?.details?.lastname || activeUser?.lastname || ""}`.trim();

  const { declarationTypes, subTypes, propertyTypes } = useSelector(
    (state: RootState) => state.itDeclarationState
  );

  const declarationTypeList = useMemo(() => {
    if (Array.isArray(declarationTypes)) return declarationTypes;
    if (Array.isArray((declarationTypes as any)?.data?.data)) return (declarationTypes as any).data.data;
    if (Array.isArray((declarationTypes as any)?.data)) return (declarationTypes as any).data;
    return [];
  }, [declarationTypes]);

  // Section Collapsed States
  const [empDetailsExpanded, setEmpDetailsExpanded] = useState(true);
  const [remarksExpanded, setRemarksExpanded] = useState(true);
  const [attachmentsExpanded, setAttachmentsExpanded] = useState(true);

  // Main Selections
  const [declarationType, setDeclarationType] = useState("");
  const [requestTyp, setRequestTyp] = useState("GA");
  const [iosSubType, setIosSubType] = useState("");
  const [propertyType, setPropertyType] = useState("");

  // Dynamic SAP Fields & Config
  const [loadingConfig, setLoadingConfig] = useState(false);
  const [dynamicFields, setDynamicFields] = useState<any[]>([]);
  const [lineFieldsConfig, setLineFieldsConfig] = useState<any[]>([]);
  const [fieldValues, setFieldValues] = useState<Record<string, any>>({});
  const [normalizedLineContentRows, setNormalizedLineContentRows] = useState<any[]>([]);

  const [selectedLineIndex, setSelectedLineIndex] = useState<number | null>(null);
  const [lineFieldValues, setLineFieldValues] = useState<Record<number, Record<string, string>>>({});
  const [attachments, setAttachments] = useState<any[]>([]);
  const [remarks, setRemarks] = useState("");

  // Evidence texts per row (for S80C / SC80)
  const [lineEvidenceTexts, setLineEvidenceTexts] = useState<Record<number, string>>({});

  // Line Item Popups State (Evidence / Detail Help)
  const [linePopup, setLinePopup] = useState<{
    kind: LinePopupKind;
    rowIndex: number;
    title: string;
    text: string;
  } | null>(null);
  const [linePopupDraft, setLinePopupDraft] = useState("");

  const [acceptTerms, setAcceptTerms] = useState(false);
  const [termsModalVisible, setTermsModalVisible] = useState(false);

  // Validation dry-run modal state
  const [validationModalVisible, setValidationModalVisible] = useState(false);
  const [validationStatus, setValidationStatus] = useState<"validating" | "success" | "error">("validating");
  const [validationMessage, setValidationMessage] = useState("");
  const [validationBullets, setValidationBullets] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    dispatch(fetchItDeclarationTypes({ empId, forceSync: false }));
  }, [dispatch, empId]);

  const normalizeFieldRows = (rows: any[], source: "header" | "line" = "header"): any[] =>
    (Array.isArray(rows) ? rows : [])
      .map((field: any) => ({
        ...field,
        key: customerFieldKey(field, source),
        label: String(field?.CustomerFieldLabel || field?.LineCustomerFieldLabel || "").replace(/\*\s*$/, "").trim(),
      }))
      .filter(
        (f: any) =>
          f.key &&
          !(String(f.key).toUpperCase() === "ATTCH" && f?.ViewOfFieldType === "T")
      )
      .sort((a: any, b: any) => Number(a?.FieldsOrder ?? 0) - Number(b?.FieldsOrder ?? 0));

  const buildContentValueMap = (rows: any[]): Record<string, any> => {
    const row = Array.isArray(rows) ? rows[0] : null;
    if (!row || typeof row !== "object") return {};
    const map: Record<string, any> = {};
    Object.keys(row).forEach((k) => {
      map[k.toUpperCase()] = row[k];
    });
    return map;
  };

  const isDateField = (field: any): boolean =>
    field?.ViewOfFieldType === "" || /^cdt/i.test(String(field?.key ?? ""));

  const yyyymmddToSapDate = (raw: any): string => {
    const str = String(raw ?? "").trim();
    if (!str || str === "00000000") return "";
    const m = str.match(/^(\d{4})(\d{2})(\d{2})$/);
    if (!m) return "";
    const ms = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    return isNaN(ms) ? "" : `/Date(${ms})/`;
  };

  const toInputDateValue = (raw: any): string => {
    if (!raw) return "";
    const str = String(raw).trim();
    if (!str || str === "00000000") return "";

    const isoMatch = str.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (isoMatch) return `${isoMatch[1]}-${isoMatch[2]}-${isoMatch[3]}`;

    const sapMatch = str.match(/\/Date\((-?\d+)\)\//);
    if (sapMatch) {
      const ms = Number(sapMatch[1]);
      if (!Number.isFinite(ms) || ms > 253402214400000) return "";
      const d = new Date(ms);
      const pad = (n: number) => String(n).padStart(2, "0");
      return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
    }

    const ymdMatch = str.match(/^(\d{4})(\d{2})(\d{2})$/);
    if (ymdMatch) {
      return `${ymdMatch[1]}-${ymdMatch[2]}-${ymdMatch[3]}`;
    }

    const dmyMatch = str.match(/^(\d{2})\.(\d{2})\.(\d{4})$/);
    if (dmyMatch) {
      return `${dmyMatch[3]}-${dmyMatch[2]}-${dmyMatch[1]}`;
    }

    const d = new Date(str);
    if (!isNaN(d.getTime())) {
      const pad = (n: number) => String(n).padStart(2, "0");
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    }

    return "";
  };

  const fetchConfigAndContent = useCallback(
    async (type: string, subType: string, propType: string, reqType: string) => {
      setLoadingConfig(true);
      const payload = {
        empId,
        declarationType: type,
        type: subType,
        propertyType: propType,
        requestType: reqType || "GA",
        operation: "EC",
      };

      try {
        const [configRes, contentRes] = await Promise.all([
          dispatch(fetchItDeclarationConfigSets(payload)).unwrap(),
          dispatch(fetchItDeclarationContentSets(payload)).unwrap(),
        ]);

        const configBody =
          configRes?.data?.ToHeaders || configRes?.data?.ToMultiLines ? configRes.data : configRes;
        const contentBody =
          contentRes?.data?.ToHeaders || contentRes?.data?.ToMultiLines ? contentRes.data : contentRes;
        const normalizedDynamic = normalizeFieldRows(configBody?.ToHeaders, "header");
        const normalizedLineFields = normalizeFieldRows(configBody?.ToMultiLines, "line");

        const headerContentMap = buildContentValueMap(contentBody?.ToHeaders);
        const lineContentRaw = contentBody?.ToMultiLines || [];

        const initialValues: Record<string, any> = {};
        normalizedDynamic.forEach((field: any) => {
          const upperKey = String(field.key).toUpperCase();
          const contentValue = headerContentMap[upperKey];
          if (contentValue !== undefined && contentValue !== null && contentValue !== "" && contentValue !== "00000000") {
            initialValues[field.key] = contentValue;
          } else {
            let rawDefault = field?.DefaultValue ?? "";
            if (!rawDefault && (upperKey === "CDT02" || /to\s*date/i.test(field.label || ""))) {
              rawDefault = "99991231";
            }
            initialValues[field.key] = isDateField(field) ? yyyymmddToSapDate(rawDefault) : rawDefault;
          }
        });

        const upperLineRows = (Array.isArray(lineContentRaw) ? lineContentRaw : []).map((row: any) => {
          const norm: Record<string, any> = {};
          if (row && typeof row === "object") {
            Object.keys(row).forEach((k) => {
              const upper = k.toUpperCase();
              if (upper !== "APAMT") norm[upper] = row[k];
            });
          }
          return norm;
        });

        const hasEditable = normalizedLineFields.some(
          (f: any) => f?.VisibleToEmployee && String(f.key).toUpperCase() !== "LINUM" && !f?.ReadOnlyForEmp
        );

        setDynamicFields(normalizedDynamic);
        setLineFieldsConfig(normalizedLineFields);
        setFieldValues(initialValues);
        setNormalizedLineContentRows(upperLineRows);
        const initialSelectedIdx = upperLineRows.findIndex((row: any) => {
          const toDateVal = String(row["CDT02"] ?? row["Cdt02"] ?? "").trim();
          return (
            toDateVal.includes("9999") ||
            toDateVal.includes("253402214400000") ||
            String(row["LINUM"] ?? row["Linum"] ?? "").trim() === "0001"
          );
        });
        setSelectedLineIndex(
          !hasEditable && upperLineRows.length > 0
            ? initialSelectedIdx !== -1
              ? initialSelectedIdx
              : 0
            : null
        );
        setLineFieldValues({});
        setLineEvidenceTexts({});
      } catch (err: any) {
        toast.error("Failed to load declaration configuration schema.");
      } finally {
        setLoadingConfig(false);
      }
    },
    [dispatch, empId]
  );

  const handleDeclarationTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const val = e.target.value;
    setDeclarationType(val);
    setIosSubType("");
    setPropertyType("");
    setDynamicFields([]);
    setLineFieldsConfig([]);
    setFieldValues({});
    setNormalizedLineContentRows([]);
    setSelectedLineIndex(null);
    setLineFieldValues({});
    setAttachments([]);
    setRemarks("");
    setLineEvidenceTexts({});
    setAcceptTerms(false);

    const selectedOpt = declarationTypeList.find(
      (item: any) => String(item?.ITDeclarationType || item?.value) === val
    );
    const reqType = selectedOpt?.RequestTyp || selectedOpt?.requestTyp || "GA";
    setRequestTyp(reqType);

    if (val) {
      if (val === "IOS") {
        dispatch(fetchItDeclarationSubTypes({ sync: true, reimbursementType: val }));
      } else {
        fetchConfigAndContent(val, "", "", reqType);
      }
    }
  };

  const handleSubTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const val = e.target.value;
    setIosSubType(val);
    setPropertyType("");
    setDynamicFields([]);
    setLineFieldsConfig([]);
    setFieldValues({});
    setNormalizedLineContentRows([]);
    setSelectedLineIndex(null);
    setLineFieldValues({});
    setLineEvidenceTexts({});

    const selectedSub = subTypes.find((s: any) => String(s?.SubType || s?.code || s?.value) === val);
    const label = selectedSub?.SubTypeText || selectedSub?.label || "";

    if (/house property/i.test(label)) {
      dispatch(fetchItDeclarationPropertyTypes({ reimbursementType: "IOS", subType: val, sync: false }));
    } else if (val) {
      fetchConfigAndContent(declarationType, val, "", requestTyp);
    }
  };

  const handlePropertyTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const val = e.target.value;
    setPropertyType(val);
    if (val) {
      fetchConfigAndContent(declarationType, iosSubType, val, requestTyp);
    }
  };

  const visibleLineColumns = useMemo(() => {
    return lineFieldsConfig.filter((f: any) => f?.VisibleToEmployee);
  }, [lineFieldsConfig]);

  const visibleDynamicFields = useMemo(() => {
    return dynamicFields.filter((f: any) => f?.VisibleToEmployee);
  }, [dynamicFields]);

  const editableLineFields = useMemo(() => {
    return visibleLineColumns.filter(
      (f: any) => String(f.key).toUpperCase() !== "LINUM" && !f?.ReadOnlyForEmp
    );
  }, [visibleLineColumns]);

  const editableLineFieldKeys = useMemo(() => {
    return new Set(editableLineFields.map((f: any) => f.key));
  }, [editableLineFields]);

  const hasEditableLineFields = editableLineFieldKeys.size > 0;

  const displayedLineRows = useMemo(() => {
    if (declarationType === "HRAC") {
      // In mobile, for Housing (HRAC), only the current / active line item (e.g. Line 0001 with To Date 31.12.9999) is displayed
      const activeRows = normalizedLineContentRows.filter((row: any) => {
        const toDateVal = String(row["CDT02"] ?? row["Cdt02"] ?? "").trim();
        return (
          toDateVal.includes("9999") ||
          toDateVal.includes("253402214400000") ||
          String(row["LINUM"] ?? row["Linum"] ?? "").trim() === "0001"
        );
      });
      return activeRows.length > 0 ? activeRows.slice(0, 1) : normalizedLineContentRows.slice(0, 1);
    }
    return normalizedLineContentRows;
  }, [declarationType, normalizedLineContentRows]);

  const handleLineValueChange = (rowIndex: number, key: string, val: string) => {
    setLineFieldValues((prev) => ({
      ...prev,
      [rowIndex]: {
        ...(prev[rowIndex] || {}),
        [key]: val,
      },
    }));
  };

  const computeLineFieldSum = useCallback(
    (key: string): string => {
      let sum = 0;
      normalizedLineContentRows.forEach((_, rowIndex) => {
        const val = parseFloat(lineFieldValues[rowIndex]?.[key] || "");
        if (!isNaN(val)) sum += val;
      });
      return sum.toFixed(2);
    },
    [lineFieldValues, normalizedLineContentRows]
  );

  const computeTotalEditableLineAmount = useCallback((): string => {
    let total = 0;
    Array.from(editableLineFieldKeys).forEach((key) => {
      normalizedLineContentRows.forEach((_, rowIndex) => {
        const val = parseFloat(lineFieldValues[rowIndex]?.[key as string] || "");
        if (!isNaN(val)) total += val;
      });
    });
    return total.toFixed(2);
  }, [editableLineFieldKeys, lineFieldValues, normalizedLineContentRows]);

  const getFieldOptions = (field: any): Option[] => {
    const raw = Array.isArray(field?.ToOptions) ? field.ToOptions : [];
    return raw
      .filter((o: any) => (o?.HeaderCustomerField || o?.CustomerFieldKey) === field?.key)
      .map((o: any) => ({
        value: String(o?.SelectedValue ?? ""),
        label: String(o?.SelectedText ?? ""),
      }));
  };

  const getLineOptionText = (field: any, raw: any): string => {
    if (raw == null || raw === "") return "";
    const rawOpts = Array.isArray(field?.ToLineOptions) ? field.ToLineOptions : [];
    const match = rawOpts.find(
      (o: any) => (o?.LineCustomerField || o?.CustomerFieldKey) === field?.key && String(o?.SelectedValue ?? "") === String(raw)
    );
    if (match) return String(match.SelectedText || match.SelectedValue);
    if (typeof raw === "string" || typeof raw === "number") return String(raw);
    return String(raw?.SelectedText ?? raw?.SelectedValue ?? raw?.value ?? "");
  };

  const getEvidenceText = (rowIndex: number): string =>
    lineEvidenceTexts[rowIndex] ??
    String(normalizedLineContentRows[rowIndex]?.[EVIDENCE_TEXT_KEY] ?? "");

  const handleOpenLinePopup = (
    kind: LinePopupKind,
    rowIndex: number,
    field: any,
    cellText: string
  ) => {
    if (kind === "evidence") {
      setLinePopupDraft(getEvidenceText(rowIndex));
    } else {
      setLinePopupDraft("");
    }
    const rowLinum = normalizedLineContentRows[rowIndex]?.LINUM;
    setLinePopup({
      kind,
      rowIndex,
      title: kind === "info" ? "Detail Help" : field.label || "Evidence",
      text:
        kind === "info"
          ? getDetailHelpText(declarationType, rowLinum, cellText)
          : cellText,
    });
  };

  const handleConfirmLinePopup = () => {
    if (linePopup?.kind === "evidence") {
      const text = linePopupDraft;
      const rowIndex = linePopup.rowIndex;
      setLineEvidenceTexts((prev) => ({ ...prev, [rowIndex]: text }));
    }
    setLinePopup(null);
  };

  // Attachments Handling
  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    const file = files[0];

    if (file.size > 2 * 1024 * 1024) {
      toast.error("File size must be less than 2MB");
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const base64String = (reader.result as string).split(",")[1];
      const fileName = file.name;
      const fileExt = fileName.split(".").pop()?.toUpperCase() || "PDF";
      const fileSize = `${Math.round(file.size / 1024)}KB`;

      setAttachments((prev) => [
        ...prev,
        {
          LineNumber: String(prev.length + 1).padStart(4, "0"),
          Content: base64String,
          FileName: fileName,
          FileExtension: fileExt,
          FileSize: fileSize,
          FileExtensionDesc: "Documents",
        },
      ]);
    };
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  const handleRemoveAttachment = (idx: number) => {
    setAttachments((prev) => prev.filter((_, i) => i !== idx));
  };

  const isAttachmentRequired = !["DTMT", "ZIOS"].includes(declarationType);

  const hasUploadedAttachment = (): boolean => {
    return (
      attachments.length > 0 &&
      attachments.some((att: any) => att?.Content && String(att.Content).trim().length > 0)
    );
  };

  const isFormComplete = (): boolean => {
    if (!declarationType) return false;
    if (!acceptTerms) return false;
    if (isAttachmentRequired && !hasUploadedAttachment()) return false;
    return !dynamicFields.some((field: any) => {
      if (!field?.CustFldMandatory || !field?.VisibleToEmployee) return false;
      if (["G", "F"].includes(field?.ViewOfFieldType)) return false;
      const val = fieldValues?.[field.key];
      return val === undefined || val === null || String(val).trim() === "";
    });
  };

  const toSapDateString = (value: any): string | null => {
    if (value === null || value === undefined || value === "") return null;
    const str = String(value).trim();
    if (!str || str === "00000000") return null;
    if (str.includes("/Date(")) return str;

    const ymdMatch = str.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (ymdMatch) {
      const ms = Date.UTC(Number(ymdMatch[1]), Number(ymdMatch[2]) - 1, Number(ymdMatch[3]));
      return isNaN(ms) ? null : `/Date(${ms})/`;
    }

    const yyyymmddMatch = str.match(/^(\d{4})(\d{2})(\d{2})$/);
    if (yyyymmddMatch) {
      const ms = Date.UTC(Number(yyyymmddMatch[1]), Number(yyyymmddMatch[2]) - 1, Number(yyyymmddMatch[3]));
      return isNaN(ms) ? null : `/Date(${ms})/`;
    }

    const dmyMatch = str.match(/^(\d{2})\.(\d{2})\.(\d{4})$/);
    if (dmyMatch) {
      const ms = Date.UTC(Number(dmyMatch[3]), Number(dmyMatch[2]) - 1, Number(dmyMatch[1]));
      return isNaN(ms) ? null : `/Date(${ms})/`;
    }

    const parsed = new Date(str);
    if (!isNaN(parsed.getTime())) {
      const ms = Date.UTC(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
      return `/Date(${ms})/`;
    }
    return null;
  };

  const putSapValue = (target: Record<string, any>, key: string, value: any) => {
    const upperKey = String(key || "").toUpperCase();
    if (!upperKey || upperKey === "APAMT") return;
    if (value !== null && typeof value === "object") return;
    target[upperKey] = value;
  };

  const buildToDynamics = () => {
    const result: Record<string, any> = {};
    dynamicFields.forEach((field: any) => {
      const upperKey = String(field.key).toUpperCase();
      if (upperKey === "RQAMT" || upperKey === "APAMT") return;
      if (field?.ViewOfFieldType === "S") {
        putSapValue(result, upperKey, computeLineFieldSum(field.key));
        return;
      }
      const value = fieldValues[field.key];
      if (field?.ViewOfFieldType === "C") {
        const checked = value === true || value === "X";
        putSapValue(result, upperKey, /^CKB(0[1-9]|10)$/.test(upperKey) ? checked : checked ? "X" : "");
        return;
      }
      if (value === "" || value === undefined || value === null) {
        putSapValue(result, upperKey, null);
      } else if (isDateField(field)) {
        putSapValue(result, upperKey, toSapDateString(value));
      } else {
        putSapValue(result, upperKey, value);
      }
    });
    return result;
  };

  const getRawLineValue = (row: any, field: any) => {
    const rawVal = row[String(field.key).toUpperCase()] ?? row[field.key];
    if (isDateField(field) && rawVal) {
      if (String(rawVal).includes("/Date(")) return rawVal;
      return toSapDateString(rawVal) || yyyymmddToSapDate(rawVal);
    }
    return rawVal === undefined ? "" : rawVal;
  };

  const buildToMultiLinesResults = (): Record<string, any>[] => {
    if (!normalizedLineContentRows.length) return [];
    const sendAllLineRows =
      (declarationType === "IOS" && iosSubType === "ST1") || ["S80C", "SC80"].includes(declarationType);
    if (hasEditableLineFields) {
      return normalizedLineContentRows
        .map((row, rowIndex) => {
          const rowObj: Record<string, any> = {};
          visibleLineColumns.forEach((field: any) => {
            const upperKey = String(field.key).toUpperCase();
            if (editableLineFieldKeys.has(field.key)) {
              const userVal = lineFieldValues[rowIndex]?.[field.key];
              const typed = isDateField(field)
                ? (toSapDateString(userVal) ?? getRawLineValue(row, field))
                : (userVal ?? "");
              putSapValue(
                rowObj,
                upperKey,
                sendAllLineRows && String(typed ?? "").trim() === "" ? "0.00" : typed
              );
            } else {
              putSapValue(rowObj, upperKey, getRawLineValue(row, field));
            }
          });
          if (["S80C", "SC80"].includes(declarationType)) {
            putSapValue(rowObj, EVIDENCE_TEXT_KEY, getEvidenceText(rowIndex));
          }
          return rowObj;
        })
        .filter((rowObj) =>
          sendAllLineRows ||
          Array.from(editableLineFieldKeys).some(
            (key) => String(rowObj[String(key).toUpperCase()] ?? "").trim() !== ""
          )
        );
    }
    if (declarationType === "IOS" || declarationType === "HRAC") {
      return normalizedLineContentRows.map((row) => {
        const rowObj: Record<string, any> = {};
        visibleLineColumns.forEach((field: any) => {
          putSapValue(rowObj, String(field.key).toUpperCase(), getRawLineValue(row, field));
        });
        return rowObj;
      });
    }
    if (selectedLineIndex == null) return [];
    const row = normalizedLineContentRows[selectedLineIndex];
    if (!row) return [];
    const rowObj: Record<string, any> = {};
    visibleLineColumns.forEach((field: any) => {
      putSapValue(rowObj, String(field.key).toUpperCase(), getRawLineValue(row, field));
    });
    return [rowObj];
  };

  const computeRequestedAmount = (): string => {
    if (
      fieldValues["RQAMT"] !== undefined &&
      fieldValues["RQAMT"] !== null &&
      String(fieldValues["RQAMT"]).trim() !== ""
    ) {
      const parsed = parseFloat(String(fieldValues["RQAMT"]));
      if (!isNaN(parsed)) return parsed.toFixed(2);
    }
    if (hasEditableLineFields) {
      const total = computeTotalEditableLineAmount();
      if (total && total !== "0.00") return total;
    }
    return "0.00";
  };

  const buildPayload = (action: "V" | "C") => ({
    Action: action,
    RequestedAmount: computeRequestedAmount(),
    ApprovedAmount: "0.00",
    RequestedNumber: "0.00",
    ApprovedNumber: "0.00",
    RequestedTaxExempt: "0.00",
    ApprovedTaxExempt: "0.00",
    UnitKey: "",
    Currency: "INR",
    ToDependants: { results: [] },
    ToRemarks: {
      results:
        declarationType === "DTMT" || !remarks.trim()
          ? []
          : [
              {
                LineNumber: "0000",
                CommentBy: "E",
                CommentedByUserID: empId,
                ApproverLevel: "00",
                EmployeeName: userName,
                Comment: remarks.trim(),
                PhaseOfTheRequest1: "N",
                RequestStatus1: "N",
                PhaseOfTheRequest2: "P",
                RequestStatus2: "T",
                Checkbox: true,
              },
            ],
    },
    ToAttachments: { results: attachments },
    ToDynamics: buildToDynamics(),
    ToMultiLines: { results: buildToMultiLinesResults() },
    ToTransactionDatas: {
      EmpID: empId,
      ReimbursementType: declarationType,
      RequestType: requestTyp || "GA",
      RequestCreatedBy: empId,
      CreatedSource: null,
      ReasonCode: null,
      AppRejDelDate: null,
      InfotypeUpdateDate: null,
      PaymentDate: null,
      CompletedDate: null,
      InfotypeBeginDate: null,
      PhaseOftheRequest: "P",
      RequestStatus: "T",
      ReferenceNumber1: null,
      TriggerRendering: iosSubType || null,
      TriggerRendering1: propertyType || null,
      OffcycleRequested: true,
      TaxExemRequired: false,
      OffcycleProvided: true,
      DeclAgreement: true,
      PartialClaim: false,
      TripCompleted: false,
      PartNumber: null,
    },
  });

  const handleValidate = async () => {
    if (!declarationType) {
      toast.error("Please select IT Declaration Type");
      return;
    }
    if (!acceptTerms) {
      toast.error("Please accept the Terms and Conditions to proceed");
      return;
    }
    if (isAttachmentRequired && !hasUploadedAttachment()) {
      toast.error("Please upload the required mandatory attachment documents to proceed.");
      setValidationModalVisible(true);
      setValidationStatus("error");
      setValidationMessage("Mandatory Attachment Missing");
      setValidationBullets([
        "Supporting documents are mandatory for this declaration type. Please upload at least one valid document before validating or submitting.",
      ]);
      return;
    }

    setValidationModalVisible(true);
    setValidationStatus("validating");
    setValidationMessage("");
    setValidationBullets([]);

    try {
      const payload = buildPayload("V");
      const res = await dispatch(submitItDeclaration(payload)).unwrap();
      const isSuccess = Boolean(res?.success) && res?.data?.success !== false;

      if (isSuccess) {
        setValidationStatus("success");
        setValidationMessage(res?.data?.message || res?.message || "Validation successful");
        setValidationBullets([]);
      } else {
        const { message, bullets } = parseBackendError(res, "Validation failed");
        setValidationStatus("error");
        setValidationMessage(message);
        setValidationBullets(bullets);
      }
    } catch (err: any) {
      const { message, bullets } = parseBackendError(err, "Validation failed. Something went wrong.");
      setValidationStatus("error");
      setValidationMessage(message);
      setValidationBullets(bullets);
    }
  };

  const handleFinalSubmit = async () => {
    setIsSubmitting(true);
    try {
      const payload = buildPayload("C");
      const res = await dispatch(submitItDeclaration(payload)).unwrap();
      const isSuccess = Boolean(res?.success) && res?.data?.success !== false;

      if (isSuccess) {
        toast.success("IT Declaration submitted successfully!");
        setValidationModalVisible(false);
        navigate(routes.itDeclaration.list);
      } else {
        const { message, bullets } = parseBackendError(res, "Failed to submit declaration.");
        setValidationStatus("error");
        setValidationMessage(message);
        setValidationBullets(bullets);
      }
    } catch (err: any) {
      const { message, bullets } = parseBackendError(err, "Submission failed. Something went wrong.");
      setValidationStatus("error");
      setValidationMessage(message);
      setValidationBullets(bullets);
    } finally {
      setIsSubmitting(false);
    }
  };

  const selectedDeclTypeObj = declarationTypeList.find(
    (t: any) => String(t?.ITDeclarationType || t?.value) === declarationType
  );
  const declarationTypeLabel =
    selectedDeclTypeObj?.ITDeclarationTypeText || selectedDeclTypeObj?.label || declarationType;

  const displaySapDate = (raw: any): string => {
    const match = String(raw ?? "").match(/\/Date\((-?\d+)\)\//);
    if (!match) return String(raw ?? "");
    const ms = Number(match[1]);
    if (!Number.isFinite(ms)) return "";
    const d = new Date(ms);
    const pad = (n: number) => String(n).padStart(2, "0");
    return `${pad(d.getUTCDate())}.${pad(d.getUTCMonth() + 1)}.${d.getUTCFullYear()}`;
  };

  return (
    <div className="page-wrapper">
      <div className="content container-fluid">
        {/* Header Breadcrumb */}
        <div className="d-md-flex align-items-center justify-content-between mb-4">
          <div>
            <h3 className="page-title mb-1 text-dark fw-bold">IT Declarations - India</h3>
            <nav aria-label="breadcrumb">
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to={routes.itDeclaration.list} className="text-primary text-decoration-none">
                    IT Declarations
                  </Link>
                </li>
                <li className="breadcrumb-item active" aria-current="page">Create Request</li>
              </ol>
            </nav>
          </div>
          <div className="d-flex align-items-center gap-2 mt-3 mt-md-0">
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary px-3 d-inline-flex align-items-center shadow-sm"
              onClick={() => navigate(routes.itDeclaration.list)}
            >
              <i className="ti ti-arrow-left me-1" /> Back to List
            </button>
          </div>
        </div>

        {/* CARD 1: Employee Details & Selection */}
        <div className="card shadow-sm border-0 mb-4 rounded-3 overflow-hidden">
          <div
            className="card-header bg-white border-bottom p-3 d-flex align-items-center justify-content-between"
            style={{ cursor: "pointer" }}
            onClick={() => setEmpDetailsExpanded(!empDetailsExpanded)}
          >
            <div className="d-flex align-items-center gap-2">
              <div
                className="avatar avatar-sm rounded-circle d-flex align-items-center justify-content-center"
                style={{ backgroundColor: "#e0f2fe", color: "#0284c7" }}
              >
                <i className="ti ti-user-cog fs-18" />
              </div>
              <div>
                <h6 className="card-title mb-0 fw-bold text-dark">Employee Details</h6>
                <small className="text-muted">Select IT declaration type and fill required information</small>
              </div>
            </div>
            <i className={`ti ti-chevron-${empDetailsExpanded ? "up" : "down"} text-muted fs-18`} />
          </div>

          {empDetailsExpanded && (
            <div className="card-body p-3">
              <div className="row g-3">
                {/* IT - Declaration Type */}
                <div className="col-lg-4 col-md-6 col-12">
                  <label className="form-label fs-12 fw-semibold text-dark mb-1">
                    IT - Declaration Type <span className="text-danger">*</span>
                  </label>
                  <select
                    className="form-select form-select-sm border-gray fs-13 py-1.5"
                    value={declarationType}
                    onChange={handleDeclarationTypeChange}
                  >
                    <option value="">Select IT Declaration Type</option>
                    {declarationTypeList.map((type: any, idx: number) => {
                      const val = type?.ITDeclarationType || type?.value || "";
                      const text = type?.ITDeclarationTypeText || type?.label || val;
                      return (
                        <option key={idx} value={val}>
                          {text}
                        </option>
                      );
                    })}
                  </select>
                </div>

                {/* IOS Sub Type */}
                {declarationType === "IOS" && subTypes.length > 0 && (
                  <div className="col-lg-4 col-md-6 col-12">
                    <label className="form-label fs-12 fw-semibold text-dark mb-1">
                      Sub Type <span className="text-danger">*</span>
                    </label>
                    <select
                      className="form-select form-select-sm border-gray fs-13 py-1.5"
                      value={iosSubType}
                      onChange={handleSubTypeChange}
                    >
                      <option value="">Select Sub Type</option>
                      {subTypes.map((sub: any, idx: number) => {
                        const val = sub?.SubType || sub?.code || sub?.value || "";
                        const text = sub?.SubTypeText || sub?.label || val;
                        return (
                          <option key={idx} value={val}>
                            {text}
                          </option>
                        );
                      })}
                    </select>
                  </div>
                )}

                {/* Property Type */}
                {iosSubType && propertyTypes.length > 0 && (
                  <div className="col-lg-4 col-md-6 col-12">
                    <label className="form-label fs-12 fw-semibold text-dark mb-1">
                      Property Type <span className="text-danger">*</span>
                    </label>
                    <select
                      className="form-select form-select-sm border-gray fs-13 py-1.5"
                      value={propertyType}
                      onChange={handlePropertyTypeChange}
                    >
                      <option value="">Select Property Type</option>
                      {propertyTypes.map((prop: any, idx: number) => {
                        const val = prop?.PropertyType || prop?.code || prop?.value || "";
                        const text = prop?.PropertyTypeText || prop?.label || val;
                        return (
                          <option key={idx} value={val}>
                            {text}
                          </option>
                        );
                      })}
                    </select>
                  </div>
                )}

                {/* SAP Dynamic Header Fields */}
                {visibleDynamicFields.map((field: any, idx: number) => {
                  const isRequired = !!field?.CustFldMandatory;
                  const isReadOnly = !!field?.ReadOnlyForEmp;
                  const fieldType = field?.ViewOfFieldType;
                  const isAmountField =
                    String(field.key).toUpperCase() === "RQAMT" ||
                    /amount/i.test(field.label || "") ||
                    fieldType === "N";
                  const options = getFieldOptions(field);

                  return (
                    <div className="col-lg-4 col-md-6 col-12" key={field.key || idx}>
                      <label className="form-label fs-12 fw-semibold text-dark mb-1">
                        {field.label} {isRequired && <span className="text-danger">*</span>}
                      </label>

                      {fieldType === "S" ? (
                        <div className="input-group input-group-sm">
                          <span className="input-group-text bg-light text-muted fs-13">₹</span>
                          <input
                            type="text"
                            className="form-control form-control-sm bg-light fw-bold text-dark fs-13 py-1.5"
                            value={computeLineFieldSum(field.key)}
                            readOnly
                          />
                        </div>
                      ) : ["E", "G", "D", "F"].includes(fieldType) ? (
                        <select
                          className={`form-select form-select-sm fs-13 py-1.5 ${isReadOnly ? "bg-light" : ""}`}
                          value={fieldValues[field.key] || ""}
                          onChange={(e) =>
                            setFieldValues((prev) => ({ ...prev, [field.key]: e.target.value }))
                          }
                          disabled={isReadOnly}
                        >
                          <option value="">Select {field.label}</option>
                          {options.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </select>
                      ) : fieldType === "C" ? (
                        <div className="form-check mt-1">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id={`check-${field.key}`}
                            checked={fieldValues[field.key] === true || fieldValues[field.key] === "X"}
                            onChange={(e) =>
                              setFieldValues((prev) => ({ ...prev, [field.key]: e.target.checked }))
                            }
                            disabled={isReadOnly}
                          />
                          <label className="form-check-label fs-12 text-dark" htmlFor={`check-${field.key}`}>
                            {field.label}
                          </label>
                        </div>
                      ) : isDateField(field) ? (
                        <input
                          type="date"
                          className={`form-control form-control-sm fs-13 py-1.5 ${isReadOnly ? "bg-light" : ""}`}
                          value={toInputDateValue(fieldValues[field.key])}
                          onChange={(e) =>
                            setFieldValues((prev) => ({ ...prev, [field.key]: e.target.value }))
                          }
                          readOnly={isReadOnly}
                        />
                      ) : isAmountField ? (
                        <div className="input-group input-group-sm">
                          <span className="input-group-text bg-light text-muted fs-13">₹</span>
                          <input
                            type="number"
                            step="0.01"
                            className={`form-control form-control-sm fs-13 py-1.5 ${isReadOnly ? "bg-light text-muted" : ""}`}
                            placeholder="0.00"
                            value={fieldValues[field.key] || ""}
                            onChange={(e) =>
                              setFieldValues((prev) => ({ ...prev, [field.key]: e.target.value }))
                            }
                            readOnly={isReadOnly}
                          />
                        </div>
                      ) : (
                        <textarea
                          className={`form-control form-control-sm fs-13 py-1.5 ${isReadOnly ? "bg-light text-muted" : ""}`}
                          rows={2}
                          value={fieldValues[field.key] || ""}
                          onChange={(e) =>
                            setFieldValues((prev) => ({ ...prev, [field.key]: e.target.value }))
                          }
                          readOnly={isReadOnly}
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* CARD 2: Line Items / Deductions Table */}
        {declarationType && visibleLineColumns.length > 0 && (
          <div className="card shadow-sm border-0 mb-4 rounded-3 overflow-hidden">
            <div className="card-header bg-white border-bottom p-3 d-flex align-items-center justify-content-between">
              <h6 className="card-title mb-0 fw-bold text-dark d-flex align-items-center gap-2">
                <i className="ti ti-table text-primary fs-18" /> Line Items
              </h6>
              {hasEditableLineFields && (
                <span className="badge bg-primary-transparent text-primary fs-12">
                  Total Proposed: ₹{computeTotalEditableLineAmount()}
                </span>
              )}
            </div>
            <div className="card-body p-0">
              {normalizedLineContentRows.length === 0 ? (
                <div className="p-4 text-center text-muted fs-13">No line items found</div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-hover align-middle mb-0 fs-13">
                    <thead className="table-light">
                      <tr>
                        {!hasEditableLineFields && declarationType === "HRAC" && (
                          <th style={{ width: "60px" }} className="text-center">Select</th>
                        )}
                        {visibleLineColumns.map((col: any) => (
                          <th key={col.key} className="fw-semibold text-dark">{col.label}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {displayedLineRows.map((row: any, idx: number) => {
                        const originalIndex = normalizedLineContentRows.indexOf(row);
                        const rowIndex = originalIndex !== -1 ? originalIndex : idx;
                        return (
                          <tr key={rowIndex} className={selectedLineIndex === rowIndex ? "table-primary" : ""}>
                            {!hasEditableLineFields && declarationType === "HRAC" && (
                              <td className="text-center">
                                <input
                                  type="radio"
                                  className="form-check-input"
                                  name="lineSelection"
                                  checked={selectedLineIndex === rowIndex}
                                  onChange={() => setSelectedLineIndex(rowIndex)}
                                />
                              </td>
                            )}
                          {visibleLineColumns.map((col: any) => {
                            const upperKey = String(col.key).toUpperCase();
                            const isEditable = editableLineFieldKeys.has(col.key);
                            const isCheckboxCell = col?.ViewOfFieldType === "C";
                            const rawVal = row[upperKey] !== undefined ? row[upperKey] : row[col.key];
                            const popupKind = getLinePopupKind(declarationType, col);
                            const cellText = isDateField(col)
                              ? displaySapDate(rawVal)
                              : getLineOptionText(col, rawVal);

                            return (
                              <td key={col.key}>
                                {isCheckboxCell ? (
                                  <span className="badge bg-light text-dark border">
                                    {rawVal === true || rawVal === "X" ? "Yes" : "No"}
                                  </span>
                                ) : isEditable ? (
                                  <input
                                    type="number"
                                    className="form-control form-control-sm"
                                    placeholder="0.00"
                                    style={{ maxWidth: "160px" }}
                                    value={lineFieldValues[rowIndex]?.[col.key] || ""}
                                    onChange={(e) =>
                                      handleLineValueChange(rowIndex, col.key, e.target.value)
                                    }
                                  />
                                ) : popupKind ? (
                                  <button
                                    type="button"
                                    className="btn btn-link text-primary p-0 text-decoration-underline fw-medium text-start fs-13"
                                    onClick={() => handleOpenLinePopup(popupKind, rowIndex, col, cellText)}
                                    title={popupKind === "evidence" ? "Click to edit evidence details" : "Click to view detail help"}
                                  >
                                    {popupKind === "evidence"
                                      ? `Particulars${getEvidenceText(rowIndex).trim() ? " (filled)" : ""}`
                                      : cellText || "-"}
                                  </button>
                                ) : (
                                  <span className="fw-medium text-dark">{cellText || "-"}</span>
                                )}
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {/* CARD 4: Remarks Section (Not shown for DTMT) */}
        {declarationType && declarationType !== "DTMT" && (
          <div className="card shadow-sm border-0 mb-4 rounded-3 overflow-hidden">
            <div
              className="card-header bg-white border-bottom p-3 d-flex align-items-center justify-content-between"
              style={{ cursor: "pointer" }}
              onClick={() => setRemarksExpanded(!remarksExpanded)}
            >
              <div className="d-flex align-items-center gap-2">
                <div
                  className="avatar avatar-sm rounded-circle d-flex align-items-center justify-content-center"
                  style={{ backgroundColor: "#fef3c7", color: "#d97706" }}
                >
                  <i className="ti ti-message-dots fs-18" />
                </div>
                <div>
                  <h6 className="card-title mb-0 fw-bold text-dark">Remarks</h6>
                  <small className="text-muted">Add your comments or remarks for the approver</small>
                </div>
              </div>
              <i className={`ti ti-chevron-${remarksExpanded ? "up" : "down"} text-muted fs-18`} />
            </div>
            {remarksExpanded && (
              <div className="card-body p-3">
                <textarea
                  className="form-control"
                  rows={3}
                  placeholder="Enter remarks..."
                  value={remarks}
                  onChange={(e) => setRemarks(e.target.value)}
                />
              </div>
            )}
          </div>
        )}

        {/* CARD 5: Upload Documents / Attachments (Not required for DTMT or ZIOS) */}
        {declarationType && !["DTMT", "ZIOS"].includes(declarationType) && (
          <div className="card shadow-sm border-0 mb-4 rounded-3 overflow-hidden">
            <div
              className="card-header bg-white border-bottom p-3 d-flex align-items-center justify-content-between"
              style={{ cursor: "pointer" }}
              onClick={() => setAttachmentsExpanded(!attachmentsExpanded)}
            >
              <div className="d-flex align-items-center gap-2">
                <div
                  className="avatar avatar-sm rounded-circle d-flex align-items-center justify-content-center"
                  style={{ backgroundColor: "#dcfce7", color: "#16a34a" }}
                >
                  <i className="ti ti-paperclip fs-18" />
                </div>
                <div>
                  <h6 className="card-title mb-0 fw-bold text-dark">
                    Upload Documents <span className="text-danger">*</span>
                  </h6>
                  <small className="text-muted">Attach supporting documents (PDF, PNG, JPG up to 2MB)</small>
                </div>
              </div>
              <i className={`ti ti-chevron-${attachmentsExpanded ? "up" : "down"} text-muted fs-18`} />
            </div>

            {attachmentsExpanded && (
              <div className="card-body p-3">
                <div className="p-3 border border-dashed rounded-3 bg-light text-center mb-3">
                  <i className="ti ti-cloud-upload fs-32 text-primary mb-1 d-block" />
                  <h6 className="fw-bold text-dark mb-1 fs-13">Choose files to upload</h6>
                  <p className="text-muted fs-12 mb-2">Accepted Formats: .PDF, .PNG, .JPG (Max size: 2MB)</p>
                  <label className="btn btn-primary btn-sm px-3 py-1.5 fs-12 cursor-pointer shadow-sm">
                    📎 Choose File
                    <input
                      type="file"
                      className="d-none"
                      accept=".pdf,.png,.jpg,.jpeg"
                      onChange={handleFileUpload}
                    />
                  </label>
                </div>

                {attachments.length > 0 ? (
                  <div className="table-responsive border rounded-3">
                    <table className="table table-hover align-middle mb-0 fs-13">
                      <thead className="table-light">
                        <tr>
                          <th>#</th>
                          <th>Document Name</th>
                          <th>File Size</th>
                          <th>Status</th>
                          <th className="text-end">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {attachments.map((file: any, idx: number) => (
                          <tr key={idx}>
                            <td>{idx + 1}</td>
                            <td className="fw-semibold text-dark">
                              <i className="ti ti-file-text me-2 text-primary fs-16" />
                              {file.FileName}
                            </td>
                            <td>{file.FileSize}</td>
                            <td>
                              <span className="badge bg-success-transparent text-success">Ready</span>
                            </td>
                            <td className="text-end">
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-danger px-2.5 py-1"
                                onClick={() => handleRemoveAttachment(idx)}
                              >
                                <i className="ti ti-trash me-1" /> Remove
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="py-2 text-center">
                    <p className="text-muted fs-13 mb-1">No documents attached yet.</p>
                    {isAttachmentRequired && (
                      <span className="text-danger fs-12 fw-medium">
                        <i className="ti ti-alert-circle me-1" />
                        Supporting document attachment is mandatory for {declarationTypeLabel}.
                      </span>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Terms and Conditions Acknowledgement Banner */}
        {declarationType && (
          <div className="card shadow-sm border-0 mb-4 bg-white rounded-3">
            <div className="card-body p-3">
              <div className="d-flex align-items-center gap-3">
                <input
                  className="form-check-input mt-0 cursor-pointer"
                  type="checkbox"
                  id="acceptTermsCheck"
                  style={{ width: "22px", height: "22px" }}
                  checked={acceptTerms}
                  onChange={(e) => setAcceptTerms(e.target.checked)}
                />
                <div>
                  <label className="form-check-label fs-13 fw-bold text-dark cursor-pointer mb-0" htmlFor="acceptTermsCheck">
                    I acknowledge and accept Terms and Conditions <span className="text-danger">*</span>
                  </label>
                  <p className="text-muted fs-12 mb-0">
                    I hereby declare that all details provided in this IT Declaration are true and correct.{" "}
                    <button
                      type="button"
                      className="btn btn-link p-0 fs-12 text-primary text-decoration-underline ms-1"
                      onClick={() => setTermsModalVisible(true)}
                    >
                      Read Terms and Conditions
                    </button>
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        {declarationType && (
          <div className="d-flex align-items-center justify-content-end gap-2 mb-4">
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary px-4 py-1.5 fs-13"
              onClick={() => navigate(routes.itDeclaration.list)}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-sm btn-primary px-4 py-1.5 fs-13 fw-semibold shadow-sm d-inline-flex align-items-center gap-1.5"
              onClick={handleValidate}
              disabled={!isFormComplete() || (validationModalVisible && validationStatus === "validating")}
            >
              {validationModalVisible && validationStatus === "validating" ? (
                <>
                  <span className="spinner-border spinner-border-sm" role="status" /> Validating...
                </>
              ) : (
                <>
                  Submit <i className="ti ti-arrow-right" />
                </>
              )}
            </button>
          </div>
        )}

        {/* Terms and Conditions Modal */}
        {termsModalVisible && (
          <div className="modal fade show d-block" tabIndex={-1} style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content border-0 shadow-lg rounded-3">
                <div className="modal-header bg-light border-bottom p-3">
                  <h5 className="modal-title fw-bold text-dark fs-16">Terms and Conditions</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setTermsModalVisible(false)}
                  />
                </div>
                <div className="modal-body p-4">
                  <p className="fs-13 text-dark mb-0 lh-lg">
                    I hereby declare that I will be abide by the Rules &amp; regulations of the Company. If any discrepancies found, I am liable to be dealt as per the Law
                  </p>
                </div>
                <div className="modal-footer bg-light border-top justify-content-end p-2.5">
                  <button
                    type="button"
                    className="btn btn-primary btn-sm px-4"
                    onClick={() => setTermsModalVisible(false)}
                  >
                    OK
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Line Item Popups: Evidence (Editable) / Detail Help (Read-only) */}
        {linePopup && (
          <div className="modal fade show d-block" tabIndex={-1} style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
            <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: "520px" }}>
              <div className="modal-content border-0 shadow-lg rounded-3">
                <div className="modal-header bg-light border-bottom p-3">
                  <h5 className="modal-title fw-bold text-dark fs-15">{linePopup.title}</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setLinePopup(null)}
                  />
                </div>
                <div className="modal-body p-4">
                  {linePopup.kind === "evidence" ? (
                    <div>
                      <label className="form-label fs-13 fw-semibold text-dark mb-1">
                        Particulars of Evidence / Details:
                      </label>
                      <textarea
                        className="form-control"
                        rows={4}
                        placeholder="Enter evidence or investment particulars..."
                        value={linePopupDraft}
                        onChange={(e) => setLinePopupDraft(e.target.value)}
                      />
                    </div>
                  ) : (
                    <div style={{ maxHeight: "320px", overflowY: "auto" }}>
                      <p className="fs-13 text-dark mb-0 lh-base">
                        {linePopup.text || "No additional description available."}
                      </p>
                    </div>
                  )}
                </div>
                <div className="modal-footer bg-light border-top justify-content-end p-2.5 gap-2">
                  {linePopup.kind === "evidence" && (
                    <button
                      type="button"
                      className="btn btn-outline-secondary btn-sm px-3"
                      onClick={() => setLinePopup(null)}
                    >
                      Cancel
                    </button>
                  )}
                  <button
                    type="button"
                    className="btn btn-primary btn-sm px-4"
                    onClick={handleConfirmLinePopup}
                  >
                    {linePopup.kind === "info" ? "Close" : "OK"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Validation Dry-Run & Submit Modal */}
        {validationModalVisible && (
          <div className="modal fade show d-block" tabIndex={-1} style={{ backgroundColor: "rgba(0,0,0,0.6)" }}>
            <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: "480px" }}>
              <div className="modal-content border-0 shadow-lg rounded-4 overflow-hidden">
                <div className="modal-body text-center p-4">
                  {validationStatus === "validating" ? (
                    <div className="py-4">
                      <div
                        className="spinner-border text-primary mb-4"
                        role="status"
                        style={{ width: "3.5rem", height: "3.5rem", borderWidth: "0.25rem" }}
                      />
                      <h5 className="fw-bold text-dark mb-2">Validating Request...</h5>
                      <p className="text-muted fs-14 mb-0">Please wait while the system checks your IT declaration.</p>
                    </div>
                  ) : validationStatus === "success" ? (
                    <div className="py-2">
                      <div className="mb-4">
                        <div
                          className="d-inline-flex align-items-center justify-content-center bg-success-transparent rounded-circle"
                          style={{ width: "72px", height: "72px" }}
                        >
                          <i className="ti ti-check text-success" style={{ fontSize: "36px" }} />
                        </div>
                      </div>
                      <h4 className="fw-bold text-dark mb-2">Validation Successful</h4>
                      <p className="text-muted fs-14 mb-4">{validationMessage || "All validation checks passed."}</p>

                      <div className="d-flex justify-content-center gap-3">
                        <button
                          type="button"
                          className="btn btn-light px-4 py-2 fw-semibold w-50"
                          onClick={() => setValidationModalVisible(false)}
                          disabled={isSubmitting}
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          className="btn btn-success px-4 py-2 fw-semibold w-50 shadow-sm"
                          onClick={handleFinalSubmit}
                          disabled={isSubmitting}
                        >
                          {isSubmitting ? (
                            <>
                              <span className="spinner-border spinner-border-sm me-2" role="status" />
                              Submitting
                            </>
                          ) : (
                            "Submit"
                          )}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="py-2">
                      <div className="mb-4">
                        <div
                          className="d-inline-flex align-items-center justify-content-center bg-danger-transparent rounded-circle"
                          style={{ width: "72px", height: "72px" }}
                        >
                          <i className="ti ti-x text-danger" style={{ fontSize: "36px" }} />
                        </div>
                      </div>
                      <h4 className="fw-bold text-dark mb-2">Validation Failed</h4>
                      {validationMessage && (
                        <p className="text-danger fs-14 mb-3" style={{ whiteSpace: "pre-line" }}>
                          {validationMessage}
                        </p>
                      )}
                      {validationBullets.length > 0 && (
                        <div
                          className="text-start bg-light p-3 rounded border mb-4"
                          style={{ maxHeight: "260px", overflowY: "auto" }}
                        >
                          {validationBullets.map((bullet, i) => (
                            <div key={i} className="text-danger fs-13 mb-2 d-flex align-items-start gap-2">
                              <span className="text-danger fw-bold fs-14" style={{ lineHeight: "1.4" }}>•</span>
                              <span style={{ lineHeight: "1.4" }}>{bullet}</span>
                            </div>
                          ))}
                        </div>
                      )}

                      <div className="d-flex justify-content-center">
                        <button
                          type="button"
                          className="btn btn-light px-5 py-2 fw-semibold w-100"
                          onClick={() => setValidationModalVisible(false)}
                          disabled={isSubmitting}
                        >
                          Close
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ITDeclarationCreatePage;
